import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { judicialService } from "@/services/api";

interface EnviadoItem {
  id: string;
  casoId?: string;
  studentName: string;
  tribunal: string;
  tipoInforme: string;
  fechaEnvio: string;
  documentoEnviado: string;
}

export default function InformesEnviadosPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enviados, setEnviados] = useState<EnviadoItem[]>([]);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");
        const data = await judicialService.list();
        if (Array.isArray(data)) {
          const mapped = data
            .filter((cr: any) => cr.estado === "respondido")
            .map((cr: any) => ({
              id: cr.id,
              casoId: cr.caso_id,
              studentName: cr.estudiante_nombre || "Estudiante",
              tribunal: cr.tribunal || "Tribunal de Familia",
              tipoInforme: cr.tipo_requerimiento || "Informe integral",
              fechaEnvio: cr.fecha_respuesta || new Date().toISOString().split("T")[0],
              documentoEnviado: `Informe_${(cr.estudiante_nombre || "Estudiante").replace(/\s+/g, "_")}.pdf`
            }));
          setEnviados(mapped);
        }
      } catch (err: any) {
        console.error("Error loading sent reports:", err);
        setError("Error al cargar informes enviados: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-2" />
        <p className="text-sm text-foreground-500">Cargando informes enviados...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Informes Enviados</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Historial de informes remitidos a tribunales</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      <div className="bg-white border border-background-200/80 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 bg-background-50">
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-400 uppercase">Estudiante</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-400 uppercase">Tribunal</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-400 uppercase">Tipo</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-400 uppercase">Fecha Envío</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-400 uppercase">Documento</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-400 uppercase">Acción</th>
              </tr>
            </thead>
            <tbody>
              {enviados.map((cr) => (
                <tr key={cr.id} className="border-b border-background-50 hover:bg-background-50 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground-900">{cr.studentName}</td>
                  <td className="py-3 px-4 text-foreground-500 text-xs">{cr.tribunal}</td>
                  <td className="py-3 px-4 text-foreground-600 capitalize text-xs">{cr.tipoInforme.replace(/_/g, " ")}</td>
                  <td className="py-3 px-4 text-green-700 text-xs font-medium">{cr.fechaEnvio}</td>
                  <td className="py-3 px-4 text-foreground-500 text-xs">{cr.documentoEnviado || "—"}</td>
                  <td className="py-3 px-4">
                    {cr.casoId && (
                      <button onClick={() => navigate(`/casos/${cr.casoId}`)} className="text-xs text-primary-600 font-medium hover:underline cursor-pointer whitespace-nowrap">
                        Ver caso →
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {enviados.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-foreground-400">
                    No se han registrado informes enviados aún.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}